import { useCallback, useMemo } from 'react';
import { PanelDefinition } from '../../../core';
import { DashboardStoreState, useDashboardStore } from './DashboardProvider';
import { getPanelKeyFromPanelGroupDefinition } from './panel-editor-slice';
import { PanelGroupId, PanelGroupItemId, PanelGroupItemLayout } from './panel-group-slice';

const selectEditMode = ({ isEditMode, setEditMode }: DashboardStoreState) => ({ isEditMode, setEditMode });
export function useEditMode() {
  return useDashboardStore(selectEditMode);
}

const selectDashboardActions = ({
  setDashboard,
  openAddPanelGroup,
  openAddPanel,
  duplicateAsSplitPanel,
  pastePanel,
  quickCreatePanel,
  quickEditPanel,
  isQuickStartDrawerOpen,
  toggleQuickStartDrawer,
}: DashboardStoreState) => ({
  setDashboard,
  openAddPanelGroup,
  openAddPanel,
  duplicateAsSplitPanel,
  pastePanel,
  quickCreatePanel,
  quickEditPanel,
  isQuickStartDrawerOpen,
  toggleQuickStartDrawer,
});
/**
 * Returns actions that can be performed on the current dashboard.
 */
export function useDashboardActions() {
  const {
    setDashboard,
    openAddPanelGroup,
    openAddPanel,
    duplicateAsSplitPanel,
    pastePanel,
    quickCreatePanel,
    quickEditPanel,
    isQuickStartDrawerOpen,
    toggleQuickStartDrawer,
  } = useDashboardStore(selectDashboardActions);
  return {
    setDashboard,
    openAddPanelGroup: () => openAddPanelGroup(),
    openAddPanel: () => openAddPanel(),
    duplicateAsSplitPanel,
    pastePanel,
    quickCreatePanel,
    quickEditPanel,
    isQuickStartDrawerOpen,
    toggleQuickStartDrawer,
  };
}

const selectPanelGroupOrder = (state: DashboardStoreState) => state.panelGroupOrder;
/**
 * Returns an array of PanelGroupIds in the order they appear in the dashboard.
 */
export function usePanelGroupIds() {
  return useDashboardStore(selectPanelGroupOrder);
}

const selectPanelGroups = (state: DashboardStoreState) => state.panelGroups;
/**
 * Returns an array of PanelGroupDefinitions in the order they appear in the dashboard.
 */
export function useListPanelGroups() {
  const panelGroupIds = usePanelGroupIds();
  const panelGroups = useDashboardStore(selectPanelGroups);
  return useMemo(() => {
    return panelGroupIds.map((id) => {
      const group = panelGroups[id];
      if (group === undefined) {
        throw new Error(`Invalid panel group Id found ${id}`);
      }
      return group;
    });
  }, [panelGroupIds, panelGroups]);
}

/**
 * Gets a specific panel group by its id. Throws if the panel group does not exist.
 */
export function usePanelGroup(panelGroupId: PanelGroupId) {
  const panelGroup = useDashboardStore(useCallback((state) => state.panelGroups[panelGroupId], [panelGroupId]));
  if (panelGroup === undefined) {
    throw new Error(`Panel group with Id ${panelGroupId} was not found`);
  }
  return panelGroup;
}

const selectPanelGroupActions = ({
  openEditPanelGroup,
  deletePanelGroup,
  openAddPanel,
  updatePanelGroupLayouts,
  duplicatePanelGroup,
  openCleanUpLayout,
}: DashboardStoreState) => ({
  openEditPanelGroup,
  deletePanelGroup,
  openAddPanel,
  updatePanelGroupLayouts,
  duplicatePanelGroup,
  openCleanUpLayout,
});
/**
 * Returns actions that can be performed on the given panel group.
 */
export function usePanelGroupActions(panelGroupId: PanelGroupId) {
  const { moveUp, moveDown } = useMovePanelGroup(panelGroupId);
  const {
    openEditPanelGroup,
    deletePanelGroup,
    openAddPanel,
    updatePanelGroupLayouts,
    duplicatePanelGroup,
    openCleanUpLayout,
  } = useDashboardStore(selectPanelGroupActions);

  return {
    openEditPanelGroup: () => openEditPanelGroup(panelGroupId),
    deletePanelGroup: () => deletePanelGroup(panelGroupId),
    openAddPanel: () => openAddPanel(panelGroupId),
    duplicatePanelGroup: () => duplicatePanelGroup(panelGroupId),
    openCleanUpLayout: () => openCleanUpLayout(panelGroupId),
    moveUp,
    moveDown,
    updatePanelGroupLayouts: (itemLayouts: PanelGroupItemLayout[]) =>
      updatePanelGroupLayouts(panelGroupId, itemLayouts),
  };
}

const selectSwapPanelGroups = (state: DashboardStoreState) => state.swapPanelGroups;
const selectPanelGroupsLength = (state: DashboardStoreState) => state.panelGroupOrder.length;
/**
 * Returns functions for moving a panel group up or down. A function will be undefined if the panel group can't be
 * moved in that direction.
 */
function useMovePanelGroup(panelGroupId: PanelGroupId) {
  const currentIndex = useDashboardStore(
    useCallback((store) => store.panelGroupOrder.findIndex((id) => id === panelGroupId), [panelGroupId])
  );
  const panelGroupsLength = useDashboardStore(selectPanelGroupsLength);
  const swapPanelGroups = useDashboardStore(selectSwapPanelGroups);

  if (currentIndex < 0) {
    throw new Error(`Could not find panel group with Id ${panelGroupId} in order array`);
  }

  const moveUp = () => swapPanelGroups(currentIndex, currentIndex - 1);
  const moveDown = () => swapPanelGroups(currentIndex, currentIndex + 1);
  return {
    moveUp: currentIndex > 0 ? moveUp : undefined,
    moveDown: currentIndex < panelGroupsLength - 1 ? moveDown : undefined,
  };
}

const selectPanelGroupEditor = (state: DashboardStoreState) => state.panelGroupEditor;
/**
 * Gets the Panel Group editor state.
 */
export function usePanelGroupEditor() {
  return useDashboardStore(selectPanelGroupEditor);
}

const selectDeletePanelGroupDialog = ({
  deletePanelGroupDialog,
  openDeletePanelGroupDialog,
  closeDeletePanelGroupDialog,
  deletePanelGroup,
}: DashboardStoreState) => ({
  deletePanelGroupDialog,
  openDeletePanelGroupDialog,
  closeDeletePanelGroupDialog,
  deletePanelGroup,
});
/**
 * Gets the Delete Panel Group dialog state.
 */
export function useDeletePanelGroupDialog() {
  const { deletePanelGroupDialog, openDeletePanelGroupDialog, closeDeletePanelGroupDialog, deletePanelGroup } =
    useDashboardStore(selectDeletePanelGroupDialog);
  return {
    deletePanelGroupDialog,
    deletePanelGroup,
    openDeletePanelGroupDialog,
    closeDeletePanelGroupDialog: () => closeDeletePanelGroupDialog(),
  };
}

/**
 * Given a PanelGroupItemID, returns the panel definition and the panel key.
 *
 * Throws if the panel can't be found.
 */
export function usePanel({ panelGroupId, panelGroupItemLayoutId }: PanelGroupItemId): {
  panelDefinition: PanelDefinition;
  panelKey: string;
} {
  const { panelDefinition, panelKey } = useDashboardStore(
    useCallback(
      (store) => {
        const panelGroup = store.panelGroups[panelGroupId];

        if (panelGroup === undefined) {
          throw new Error(`Could not find panel group for panelGroupId ${panelGroupId}`);
        }

        const panelKey = getPanelKeyFromPanelGroupDefinition(panelGroup, panelGroupItemLayoutId);

        return {
          panelKey,
          panelDefinition: panelKey === undefined ? undefined : store.panels[panelKey],
        };
      },
      [panelGroupId, panelGroupItemLayoutId]
    )
  );

  if (panelDefinition === undefined || panelKey === undefined) {
    throw new Error(
      `Could not find panel for panelGroupId ${panelGroupId} and panelGroupItemLayoutId ${panelGroupItemLayoutId}`
    );
  }

  return { panelDefinition, panelKey };
}

/**
 * Given a panel key, return the panel definition.
 */
export function usePanelByKey(panelKey?: string): {
  panelDefinition?: PanelDefinition;
} {
  const { panelDefinition } = useDashboardStore(
    useCallback(
      (store) => {
        return {
          panelDefinition: panelKey === undefined ? undefined : store.panels[panelKey],
        };
      },
      [panelKey]
    )
  );

  return { panelDefinition };
}

const selectPanelActions = ({
  openEditPanel,
  openDeletePanelDialog,
  deletePanel,
  duplicatePanel,
  editPanelDefinition,
  movePanelToPanelGroup,
}: DashboardStoreState) => ({
  openEditPanel,
  openDeletePanelDialog,
  deletePanel,
  duplicatePanel,
  editPanelDefinition,
  movePanelToPanelGroup,
});

/**
 * Returns actions that can be performed on the given Panel.
 */
export function usePanelActions(panelGroupItemId: PanelGroupItemId) {
  const {
    openEditPanel,
    openDeletePanelDialog,
    deletePanel,
    duplicatePanel,
    editPanelDefinition,
    movePanelToPanelGroup,
  } = useDashboardStore(selectPanelActions);
  return {
    openEditPanel: () => openEditPanel(panelGroupItemId),
    openDeletePanelDialog: () => openDeletePanelDialog(panelGroupItemId),
    deletePanel: () => deletePanel(panelGroupItemId),
    duplicatePanel: () => duplicatePanel(panelGroupItemId),
    editPanelDefinition: (definition: PanelDefinition) => editPanelDefinition(panelGroupItemId, definition),
    movePanelToPanelGroup: (panelKey: string, newGroupId: number) =>
      movePanelToPanelGroup(
        panelGroupItemId.panelGroupId,
        panelGroupItemId.panelGroupItemLayoutId,
        panelKey,
        newGroupId
      ),
  };
}

const selectPanelEditor = (state: DashboardStoreState) => state.panelEditor;
/**
 * Gets the state for the Panel Editor.
 */
export function usePanelEditor() {
  return useDashboardStore(selectPanelEditor);
}

const selectDeletePanelDialog = ({ deletePanelDialog, deletePanel, closeDeletePanelDialog }: DashboardStoreState) => ({
  deletePanelDialog,
  deletePanel,
  closeDeletePanelDialog,
});

/**
 * Gets the state for the "Delete panel" dialog.
 */
export function useDeletePanelDialog() {
  // TODO: Refactor similar to other dialogs/editors so these are on the editor state itself
  return useDashboardStore(selectDeletePanelDialog);
}

const selectDashboardDuration = (state: DashboardStoreState) => state.duration;
export function useDashboardDuration() {
  return useDashboardStore(selectDashboardDuration);
}

const selectDashboardTime = (state: DashboardStoreState) => state.time;
export function useDashboardTime() {
  return useDashboardStore(selectDashboardTime);
}

const selectSaveChangesConfirmationDialog = ({
  saveChangesConfirmationDialog,
  openSaveChangesConfirmationDialog,
  closeSaveChangesConfirmationDialog,
}: DashboardStoreState) => ({
  saveChangesConfirmationDialog,
  openSaveChangesConfirmationDialog,
  closeSaveChangesConfirmationDialog,
});
export function useSaveChangesConfirmationDialog() {
  return useDashboardStore(selectSaveChangesConfirmationDialog);
}

const selectDiscardChangesConfirmationDialog = ({
  discardChangesConfirmationDialog,
  openDiscardChangesConfirmationDialog,
  closeDiscardChangesConfirmationDialog,
}: DashboardStoreState) => ({
  discardChangesConfirmationDialog,
  openDiscardChangesConfirmationDialog,
  closeDiscardChangesConfirmationDialog,
});
export function useDiscardChangesConfirmationDialog() {
  return useDashboardStore(selectDiscardChangesConfirmationDialog);
}

const selectEditJsonDialog = ({ editJsonDialog, openEditJsonDialog, closeEditJsonDialog }: DashboardStoreState) => ({
  editJsonDialog,
  openEditJsonDialog,
  closeEditJsonDialog,
});
/**
 * Gets the state for the edit JSON dialog.
 */
export function useEditJsonDialog() {
  return useDashboardStore(selectEditJsonDialog);
}

/**
 * Gets the state for dashboard metadata.
 */
const selectMetadata = ({ metadata, setMetadata }: DashboardStoreState) => ({
  metadata,
  setMetadata,
});
export function useDashboardMetadata() {
  return useDashboardStore(selectMetadata);
}

/**
 * Gets the state for the dashboard settings drawer.
 */
const selectSettingsDrawer = ({
  isSettingsDrawerOpen,
  openSettingsDrawer,
  closeSettingsDrawer,
}: DashboardStoreState) => ({
  isSettingsDrawerOpen,
  openSettingsDrawer,
  closeSettingsDrawer,
});

export function useSettingsDrawer() {
  return useDashboardStore(selectSettingsDrawer);
}

/**
 * Gets the state for the clean-up layout drawer.
 */
const selectCleanUpLayoutDrawer = ({
  isCleanUpLayoutDrawerOpen,
  cleanUpLayoutPanelGroupId,
  openCleanUpLayout,
  closeCleanUpLayout,
}: DashboardStoreState) => ({
  isCleanUpLayoutDrawerOpen,
  cleanUpLayoutPanelGroupId,
  openCleanUpLayout,
  closeCleanUpLayout,
});

export function useCleanUpLayoutDrawer() {
  return useDashboardStore(selectCleanUpLayoutDrawer);
}

/**
 * Gets the state for dashboard focus management.
 */
const selectDashboardFocus = ({
  focusedPanelGroupId,
  setFocusedPanelGroupId,
  isFocusEnabled,
}: DashboardStoreState) => ({
  focusedPanelGroupId,
  setFocusedPanelGroupId,
  isFocusEnabled,
});

/**
 * Gets info about user's focus state, currently used to determine which panel group is being hovered over.
 * Later on we could track other types of focus state for the dashboard.
 */
export function useDashboardFocus() {
  return useDashboardStore(selectDashboardFocus);
}

/**
 * State for managing history of actions performed on the dashboard.
 */
const selectUndoRedo = ({ undo, redo, canUndo, canRedo, getLastAction, clearActions }: DashboardStoreState) => ({
  undo,
  redo,
  canUndo,
  canRedo,
  getLastAction,
  clearActions,
});

export function useUndoRedo() {
  return useDashboardStore(selectUndoRedo);
}
